import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import abTestService, { ABTest, ABTestMetrics } from '@/services/abTestService';

interface ABTestStore {
  // State
  tests: ABTest[];
  currentTest: ABTest | null;
  metrics: ABTestMetrics[];
  isLoading: boolean;
  error: string | null;

  // Actions
  fetchUserTests: (userId: string) => Promise<void>;
  fetchTestById: (testId: string) => Promise<void>;
  fetchTestMetrics: (testId: string, inicio?: string, fim?: string) => Promise<void>;
  createTest: (data: {
    name: string;
    id_user: string;
    videosIds: {
      id_video: string;
      trafego: number;
    }[];
    copy?: boolean;
  }) => Promise<ABTest | null>;
  updateTest: (testId: string, data: {
    name?: string;
    videosIds?: {
      id_video: string;
      trafego: number;
    }[];
  }) => Promise<void>;
  deleteTest: (testId: string) => Promise<void>;
  setCurrentTest: (test: ABTest | null) => void;
  resetError: () => void;
  reset: () => void;
}

const initialState = {
  tests: [],
  currentTest: null,
  metrics: [],
  isLoading: false,
  error: null,
};

export const useABTestStore = create<ABTestStore>()(
  devtools(
    (set, get) => ({
      ...initialState,

      fetchUserTests: async (userId: string) => {
        set({ isLoading: true, error: null });
        try {
          const tests = await abTestService.getUserABTests(userId);
          set({ tests, isLoading: false });
        } catch (error: any) {
          console.error('Erro ao buscar testes A/B:', error);
          set({
            error: error?.message || 'Erro ao buscar testes A/B',
            isLoading: false,
          });
        }
      },

      fetchTestById: async (testId: string) => {
        set({ isLoading: true, error: null });
        try {
          const videos = await abTestService.getTestVideos(testId);
          // Como não temos endpoint específico para buscar um teste,
          // vamos buscar os vídeos e construir o teste a partir deles
          const currentTest = get().tests.find(t => t.id === testId);
          if (currentTest) {
            set({
              currentTest: { ...currentTest, videos },
              isLoading: false,
            });
          } else {
            set({ isLoading: false });
          }
        } catch (error: any) {
          console.error('Erro ao buscar teste A/B:', error);
          set({
            error: error?.message || 'Erro ao buscar teste A/B',
            isLoading: false,
          });
        }
      },

      fetchTestMetrics: async (testId: string, inicio?: string, fim?: string) => {
        set({ isLoading: true, error: null });
        try {
          const metrics = await abTestService.getTestMetrics(testId, inicio, fim);
          set({ metrics, isLoading: false });
        } catch (error: any) {
          console.error('Erro ao buscar métricas do teste:', error);
          set({
            error: error?.message || 'Erro ao buscar métricas do teste',
            isLoading: false,
          });
        }
      },

      createTest: async (data) => {
        set({ isLoading: true, error: null });
        try {
          const newTest = await abTestService.createABTest(data);
          set((state) => ({
            tests: [...state.tests, newTest],
            currentTest: newTest,
            isLoading: false,
          }));
          return newTest;
        } catch (error: any) {
          console.error('Erro ao criar teste A/B:', error);
          const message =
            error?.response?.data?.message ||
            error?.message ||
            'Erro ao criar teste A/B';
          set({
            error: message,
            isLoading: false,
          });
          throw new Error(message);
        }
      },

      updateTest: async (testId, data) => {
        set({ isLoading: true, error: null });
        try {
          const updatedTest = await abTestService.updateABTest(testId, data);
          set((state) => ({
            tests: state.tests.map((t) =>
              t.id === testId ? updatedTest : t
            ),
            currentTest:
              state.currentTest?.id === testId
                ? updatedTest
                : state.currentTest,
            isLoading: false,
          }));
        } catch (error: any) {
          console.error('Erro ao atualizar teste A/B:', error);
          set({
            error: error?.message || 'Erro ao atualizar teste A/B',
            isLoading: false,
          });
        }
      },

      deleteTest: async (testId) => {
        set({ isLoading: true, error: null });
        try {
          await abTestService.deleteABTest(testId);
          set((state) => ({
            tests: state.tests.filter((t) => t.id !== testId),
            currentTest:
              state.currentTest?.id === testId
                ? null
                : state.currentTest,
            isLoading: false,
          }));
        } catch (error: any) {
          console.error('Erro ao deletar teste A/B:', error);
          set({
            error: error?.message || 'Erro ao deletar teste A/B',
            isLoading: false,
          });
        }
      },

      setCurrentTest: (test) => {
        set({ currentTest: test });
      },

      resetError: () => {
        set({ error: null });
      },

      reset: () => {
        set(initialState);
      },
    }),
    { name: 'ABTestStore' }
  )
);
